import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  TypeDeliveryAgencyTrackingFilterOptions,
  TypeGetShippingLabelsSearchConditionsResponse,
} from '~/types/delivery-agency.types';

/**
 * [운송장 관리] 검색 필터 코드 조회 API
 */
export const fetchShippingLabelsSearchConditions = async (): Promise<
  TypeGetShippingLabelsSearchConditionsResponse['data']
> => {
  const { data } = await axiosInstance.get<TypeGetShippingLabelsSearchConditionsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_SHIPPING_LABELS_CODES
  );
  return data.data;
};

/**
 * [운송장 관리] 검색 필터 코드 조회 Query Hook
 */
export const useGetShippingLabelsSearchConditionsQuery = (options?: { enabled?: boolean; silent?: boolean }) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.shippingLabelsSearchConditions(),
    queryFn: fetchShippingLabelsSearchConditions,
    staleTime: 1000 * 60 * 30,
    enabled: options?.enabled ?? true,
    meta: { silent: options?.silent },
    select: (data): TypeDeliveryAgencyTrackingFilterOptions => ({
      searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
      trackingStatuses: transformToSelectOptions(data.shippingLabelStatuses, 'code', 'description'),
      deliveryFailureReasonCategories: transformToSelectOptions(
        data.deliveryFailureReasonCategories,
        'code',
        'description'
      ),
      deliveryTypes: transformToSelectOptions(data.deliveryTypes, 'code', 'description'),
      courierCodes: transformToSelectOptions(data.courierCodes, 'code', 'description'),
    }),
  });
};

/**
 * [운송장 관리] 검색 필터 코드 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<TypeGetShippingLabelsSearchConditionsResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/shipping-labels/search-conditions',
  label: '운송장 관리/검색 필터 코드',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          searchKeywordTypes: [
            { code: 'SHIPPING_LABEL_NUMBER', description: '운송장 번호' },
            { code: 'OUTBOUND_CODE', description: '출고번호' },
            { code: 'CLIENT_ORDER_CODE', description: '판매처 주문번호' },
            { code: 'RECEIVER_NAME', description: '수령자명' },
            { code: 'ORDERER_NAME', description: '주문자명' },
            { code: 'PRODUCT_NAME', description: '상품명' },
          ],
          shippingLabelStatuses: [
            { code: 'OUTBOUND_READY', description: '출고 대기' },
            { code: 'OUTBOUND_PROCESSING', description: '출고 진행중' },
            { code: 'ON_THE_WAY', description: '배송 중' },
            { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
            { code: 'DELIVERY_CANCELED', description: '배송 취소' },
            { code: 'DELIVERY_FAILED', description: '배송 실패' },
          ],
          deliveryTypes: [
            { code: 'DAWN', description: '샛별배송' },
            { code: 'DAY', description: '하루배송' },
          ],
          courierCodes: [
            { code: 'NM', description: '넥스트마일' },
            { code: 'CJT', description: 'CJ' },
            { code: 'LTT', description: '롯데' },
          ],
          deliveryFailureReasonCategories: [
            { code: 'SELLER_NOT_SHIPPED', description: '판매자 미출고' },
            { code: 'SELLER_SHIPPING_ISSUE', description: '판매자 출고 이슈' },
            { code: 'DELIVERY_PROCESS_ISSUE', description: '배송 중 상품 분실/파손' },
            { code: 'ADDRESS_ERROR', description: '주소 오류' },
            { code: 'NO_ENTRY', description: '배송지 진입 불가' },
            { code: 'BAD_WEATHER', description: '기상 악화' },
            { code: 'CUSTOMER_REQUEST', description: '고객 요청' },
            { code: 'NO_ITEM_TO_RETURN', description: '회수 상품 없음' },
          ],
        },
      },
    },
  ],
});
